import React, { useState, useRef, useEffect } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import {
  FiBriefcase,
  FiRefreshCw,
  FiZap,
  FiClock,
  FiCalendar,
  FiCoffee,
  FiTag,
  FiPackage,
  FiBox,
  FiLayers,
  FiTrendingUp,
} from 'react-icons/fi';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { IconSpinner } from '../../components';
import css from './EditProjectListingBudgetForm.css';

export const STEPS = [
  {
    field: 'projectDuration',
    titleId: 'EditProjectListingBudgetForm.projectDurationTitle',
    options: [
      {
        value: 'project',
        labelId: 'EditProjectListingBudgetForm.projectDurationProject',
        Icon: FiBriefcase,
      },
      {
        value: 'continuous',
        labelId: 'EditProjectListingBudgetForm.projectDurationContinuous',
        Icon: FiRefreshCw,
      },
    ],
  },
  {
    field: 'projectUrgency',
    titleId: 'EditProjectListingBudgetForm.projectUrgencyTitle',
    options: [
      {
        value: 'urgent',
        labelId: 'SearchFilters.urgencyUrgent',
        Icon: FiZap,
      },
      {
        value: 'within_a_week',
        labelId: 'SearchFilters.urgencyWithinAWeek',
        Icon: FiClock,
      },
      {
        value: 'within_a_month',
        labelId: 'SearchFilters.urgencyWithinAMonth',
        Icon: FiCalendar,
      },
      {
        value: 'not_urgent',
        labelId: 'SearchFilters.urgencyNotUrgent',
        Icon: FiCoffee,
      },
    ],
  },
  {
    field: 'priceEstimate',
    titleId: 'EditProjectListingBudgetForm.pricePerUnit',
    options: [
      {
        value: '0-1 000 €',
        label: '0–1 000 €',
        Icon: FiTag,
      },
      {
        value: '1 000-3 000 €',
        label: '1 000–3 000 €',
        Icon: FiPackage,
      },
      {
        value: '3 000-5 000 €',
        label: '3 000–5 000 €',
        Icon: FiBox,
      },
      {
        value: '5 000-10 000 €',
        label: '5 000–10 000 €',
        Icon: FiLayers,
      },
      {
        value: '10 000 € +',
        label: '10 000 € +',
        Icon: FiTrendingUp,
      },
    ],
  },
];

const PICK_HIGHLIGHT_MS = 150;
const SLIDE_MS = 250;

export const EditProjectListingBudgetFormComponent = props => {
  const { className, intl, initialValues, onSubmit, updateInProgress, fetchErrors } = props;

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState({
    projectDuration: initialValues?.projectDuration,
    projectUrgency: initialValues?.projectUrgency,
    priceEstimate: initialValues?.priceEstimate,
  });
  const [pickedValue, setPickedValue] = useState(null);
  // 'idle' | 'out-forward' | 'in-forward' | 'out-back' | 'in-back'
  const [phase, setPhase] = useState('idle');
  const submittedRef = useRef(false);

  const step = STEPS[stepIndex];
  const totalSteps = STEPS.length;
  const isLastStep = stepIndex === totalSteps - 1;
  const isAnimating = phase !== 'idle';

  const { updateListingError, showListingsError } = fetchErrors || {};

  useEffect(() => {
    if (updateListingError) {
      submittedRef.current = false;
      setPhase('idle');
    }
  }, [updateListingError]);

  const advanceForward = nextValues => {
    if (isLastStep) {
      if (submittedRef.current) return;
      submittedRef.current = true;
      onSubmit({
        price: initialValues?.price,
        projectDuration: nextValues.projectDuration,
        projectUrgency: nextValues.projectUrgency,
        priceEstimate: nextValues.priceEstimate,
      });
      return;
    }
    setStepIndex(idx => idx + 1);
    setPhase('in-forward');
    setPickedValue(null);
    window.setTimeout(() => setPhase('idle'), SLIDE_MS);
  };

  const handlePick = optionValue => {
    if (isAnimating || (isLastStep && updateInProgress)) return;
    setPickedValue(optionValue);
    const nextValues = { ...values, [step.field]: optionValue };
    setValues(nextValues);

    window.setTimeout(() => {
      setPhase('out-forward');
      window.setTimeout(() => advanceForward(nextValues), SLIDE_MS);
    }, PICK_HIGHLIGHT_MS);
  };

  const handleBack = () => {
    if (isAnimating || stepIndex === 0) return;
    setPhase('out-back');
    window.setTimeout(() => {
      setStepIndex(idx => idx - 1);
      setPhase('in-back');
      setPickedValue(null);
      window.setTimeout(() => setPhase('idle'), SLIDE_MS);
    }, SLIDE_MS);
  };

  const stepClasses = classNames(css.step, {
    [css.stepOutForward]: phase === 'out-forward',
    [css.stepInForward]: phase === 'in-forward',
    [css.stepOutBack]: phase === 'out-back',
    [css.stepInBack]: phase === 'in-back',
  });

  const title = intl.formatMessage({ id: step.titleId });
  const submitting = isLastStep && updateInProgress;

  return (
    <div className={classNames(css.root, className)}>
      {updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditProjectListingBudgetForm.updateFailed" />
        </p>
      ) : null}
      {showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditProjectListingBudgetForm.showListingFailed" />
        </p>
      ) : null}

      <div className={css.chrome}>
        <button
          type="button"
          className={css.backButton}
          onClick={handleBack}
          disabled={stepIndex === 0 || isAnimating}
          aria-label={intl.formatMessage({ id: 'EditProjectListingBudgetForm.stepBack' })}
        >
          <span aria-hidden="true">←</span>{' '}
          <FormattedMessage id="EditProjectListingBudgetForm.stepBack" />
        </button>
        <span className={css.stepCounter}>
          <FormattedMessage
            id="EditProjectListingBudgetForm.stepCounter"
            values={{ current: stepIndex + 1, total: totalSteps }}
          />
        </span>
      </div>

      <div className={css.stepWrapper}>
        <div className={stepClasses} key={step.field}>
          <h2 className={css.title}>{title}</h2>

          <div className={css.options}>
            {step.options.map(opt => {
              const label = opt.labelId ? intl.formatMessage({ id: opt.labelId }) : opt.label;
              const isPicked = pickedValue === opt.value;
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={classNames(css.option, { [css.optionPicked]: isPicked })}
                  onClick={() => handlePick(opt.value)}
                  disabled={isAnimating || submitting}
                >
                  {Icon ? (
                    <span className={css.optionIcon} aria-hidden="true">
                      <Icon size={22} />
                    </span>
                  ) : null}
                  <span className={css.optionText}>
                    <span className={css.optionLabel}>{label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {submitting ? (
            <div className={css.submittingRow}>
              <IconSpinner />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

EditProjectListingBudgetFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  initialValues: {},
};

EditProjectListingBudgetFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  initialValues: object,
  onSubmit: func.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditProjectListingBudgetFormComponent);
